import React, { useState } from 'react';
import { Box, Typography, Link as MuiLink } from '@mui/material';
import { Link, useHistory } from 'react-router-dom';

import PoweredByDQDefault from '../../../assets/images/PoweredByDQ_Default.png';
import PoweredByDQWhite from '../../../assets/images/PoweredByDQ_white.png';
import { useColorMode } from '../../../context/colorMode/colorMode.context';
import useStyles from '../../../assets/styles/CurationPromptApplicationStyles';
import { lookupSignupStatusService } from '../../../services/pbdqSignup.service';
import { TextInput, StepActions, ErrorList, ORANGE, MUTED, BORDER } from './components/SignupFields.comp';
import { STATUS_PAGE, statusMessage } from './signup.text';
import { saveToken } from './signup.session';

/**
 * PbdqStatus — /pbdq/status (public, no login).
 *
 * Anyone who applied through the sign-up chatbot checks where their application
 * stands with its reference and the email they applied with (product-owner decision,
 * 17 Sep 2026). The API answers only when both match, and shows no personal details.
 *
 * When there is a step to take — pay, or upload documents for a DQ Pro review — the
 * API issues a token; it goes into sessionStorage (never the URL) and the button opens
 * the sign-up chatbot at that step.
 */

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : null);

const PbdqStatusPage = () => {
    const classes = useStyles();
    const { isDark } = useColorMode();
    const history = useHistory();

    const [reference, setReference] = useState('');
    const [email, setEmail] = useState('');
    const [busy, setBusy] = useState(false);
    const [errors, setErrors] = useState([]);
    const [result, setResult] = useState(null);

    const check = async () => {
        setErrors([]);
        if (!reference.trim() || !email.trim()) { setErrors([STATUS_PAGE.MISSING]); return; }
        setBusy(true);
        try {
            const res = await lookupSignupStatusService(reference.trim(), email.trim());
            if (res?.data) setResult(res.data);
        } catch (e) {
            // invokeApi has already shown the reason; keep it under the form as well.
            setResult(null);
            setErrors([e?.message || STATUS_PAGE.NOT_FOUND]);
        } finally {
            setBusy(false);
        }
    };

    const open = (query) => {
        const ref = result.application.reference_no;
        saveToken(ref, result.token);
        history.push(`/powered-by-dq/chatbot?ref=${encodeURIComponent(ref)}&${query}`);
    };

    const a = result?.application;
    const message = a ? statusMessage(a) : null;
    const submitted = a ? fmtDate(a.submitted_at || a.created_at) : null;

    return (
        <Box className={classes.pageWrapper} sx={{ overflow: 'auto !important' }}>
            <Box className={classes.header}>
                <MuiLink component={Link} to="/" style={{ lineHeight: 0, flexShrink: 0 }}>
                    <img src={isDark ? PoweredByDQWhite : PoweredByDQDefault} alt="Powered by DQ" className={classes.logo}/>
                </MuiLink>
                <Box className={classes.headerCenter}>
                    <Typography style={{ fontWeight: 800, fontSize: 15, color: 'var(--brand-text, #1a1a1a)', lineHeight: 1.2, letterSpacing: '0.01em' }}>
                        {STATUS_PAGE.TITLE}
                    </Typography>
                    <Typography style={{ fontWeight: 400, fontSize: 12, color: 'var(--brand-text-muted, #888)', lineHeight: 1.3, marginTop: 2 }}>
                        AI &amp; Digital Literacy Solutions by DQ
                    </Typography>
                </Box>
                <Box className={classes.headerRight}>
                    <Typography variant="body2" style={{ color: 'var(--brand-text-muted, #555)', whiteSpace: 'nowrap' }}>
                        Already have an account?{' '}
                        <MuiLink component={Link} to="/login" color="primary" style={{ fontWeight: 600 }}>Log in</MuiLink>
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ width: '100%', maxWidth: 520, mx: 'auto', px: 2, py: { xs: 3, sm: 6 } }}>
                <Box sx={{ p: { xs: 2, sm: 3 }, borderRadius: '16px', background: 'var(--brand-surface, #fff)', border: `1px solid ${BORDER}` }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', mb: 0.5 }}>{STATUS_PAGE.HEADING}</Typography>
                    <Typography sx={{ fontSize: '0.88rem', color: MUTED, mb: 2.5 }}>{STATUS_PAGE.INTRO}</Typography>
                    <Box
                        component="form"
                        onSubmit={(e) => { e.preventDefault(); check(); }}
                        sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}
                    >
                        <TextInput label="Application reference*" value={reference} onChange={setReference} placeholder="PBDQ-2026-00001" inputProps={{ maxLength: 40 }}/>
                        <TextInput label="Email address*" type="email" value={email} onChange={setEmail} placeholder="The email you applied with" inputProps={{ maxLength: 254 }}/>
                        <ErrorList errors={errors}/>
                        <StepActions nextLabel="Check status" onNext={check} busy={busy}/>
                    </Box>
                </Box>

                {a && (
                    <Box sx={{ mt: 2.5, p: { xs: 2, sm: 3 }, borderRadius: '16px', background: 'var(--brand-surface, #fff)', border: `1px solid ${BORDER}` }}>
                        <Typography sx={{ fontSize: '0.78rem', color: MUTED }}>Reference {a.reference_no}{submitted ? ` · ${submitted}` : ''}</Typography>
                        <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', mt: 0.5 }}>{a.solution_label}</Typography>
                        <Box sx={{ display: 'inline-block', mt: 1.25, px: 1.25, py: 0.4, borderRadius: '999px', background: '#FFF4ED', color: ORANGE, fontWeight: 700, fontSize: '0.8rem' }}>
                            {message.label}
                        </Box>
                        {message.lines.map((l) => (
                            <Typography key={l} sx={{ fontSize: '0.9rem', lineHeight: 1.6, mt: 1.25 }}>{l}</Typography>
                        ))}
                        {(result.actions.pay || result.actions.upload_documents || message.login) && (
                            <StepActions
                                onNext={result.actions.pay ? () => open('pay=1')
                                    : result.actions.upload_documents ? () => open('upload=1')
                                        : () => history.push('/login')}
                                nextLabel={result.actions.pay ? STATUS_PAGE.PAY
                                    : result.actions.upload_documents ? STATUS_PAGE.UPLOAD
                                        : STATUS_PAGE.LOGIN}
                            />
                        )}
                    </Box>
                )}

                <Typography sx={{ fontSize: '0.8rem', color: MUTED, textAlign: 'center', mt: 3 }}>
                    {STATUS_PAGE.HELP}
                </Typography>
            </Box>
        </Box>
    );
};

export default PbdqStatusPage;
